/* eslint-disable max-lines */
import type { Dispatch, SetStateAction } from 'react';
import { useEffect } from 'react';

import type {
    NavigateWarningReducerAction,
    NavigateWarningReducerState,
} from './navigateWarningReducer';
import type {
    ProgressModalConfig,
    ProgressReducerAction,
} from './progressReducer';
import type {
    SnackbarReducerAction,
    SnackbarReducerState,
} from './snackbarReducer';
import type { ThemeReducerAction, ThemeReducerState } from './themeReducer';
import type { AuthenticationResult } from '@azure/msal-common';
import type { Theme } from '@mui/material/styles';

import {
    type BomCombinationRuleset,
    type Brand,
    type BuyingProfile,
    type MsalProfile,
    type UserBrand,
} from '@buying/library';
import { createTheme } from '@mui/material/styles';
import getHookAndProviderFromSlices, {
    defineSlice,
} from 'react-context-slices';

import {
    cardLayersReducer,
    type CardLayersReducerAction,
    type CardLayersReducerState,
} from './cardLayersReducer';
import {
    guidesReducer,
    type GuidesReducerAction,
    type GuidesReducerState,
} from './hooks/useGuideManager/guidesReducer';
import { getInitialGuidesState } from './hooks/useGuideManager/guidesState';
import { navigateWarningReducer } from './navigateWarningReducer';
import { INACTIVE_PROGRESS_CONFIG, progressReducer } from './progressReducer';
import { snackbarReducer } from './snackbarReducer';
import { defaultThemeState, themeReducer } from './themeReducer';
import {
    BACKEND_NAMES,
    type BackendBranches,
    getInitialBackendBranches,
} from './utils/backendBranches';

type BrandPickerMode = 'hidden' | 'multiple' | 'single';

interface BomCombinationRulesetContext {
    bomCombinationRuleset: BomCombinationRuleset | undefined;
    allUsages: Array<Record<string, unknown> & { usageName: string }>;
    allMaterialSubcategories: Array<
        Record<string, unknown> & { materialSubcategoryName: string }
    >;
    allPlacements: Array<Record<string, unknown> & { placementName: string }>;
    masterStyleSubcategoryNumber: string;
    masterStyleSubcategoryName: string;
    masterStyleCategoryName: string;
}

// The GlobalStateKey type is used to define the keys of our global state, which provides Typescript autocompletion.
interface GlobalStateKey {
    backendBranches: BackendBranches;
    bomCombinationRuleset: BomCombinationRulesetContext | undefined;
    buyingProfile: BuyingProfile | undefined;
    cardLayers: CardLayersReducerState;
    demoMode: boolean;
    guides: GuidesReducerState;
    isBomOptionsDialogOpen: boolean;
    msalAuthenticationResult: AuthenticationResult | undefined;
    msalProfile: MsalProfile | undefined;
    navigateWarning: NavigateWarningReducerState;
    // Devbar-only frontend permission simulation, keyed by permissionName; absent key = inherit backend value.
    permissionOverrides: Record<string, boolean>;
    progressModal: ProgressModalConfig;
    brandPickerMode: BrandPickerMode;
    isResultCountVisible: boolean;
    isSearchBarVisible: boolean;
    resultCount: number;
    searchPlaceholder: string;
    searchQuery: string;
    selectedBrands: Array<UserBrand>;
    snackbarQueue: SnackbarReducerState;
    theme: Theme;
    themeMode: ThemeReducerState;
}

// The GlobalStateKey type is used to define the keys of our global state actions, which provides Typescript autocompletion.
interface GlobalActionKey {
    cardLayers: CardLayersReducerAction;
    guides: GuidesReducerAction;
    navigateWarning: NavigateWarningReducerAction;
    progressModal: ProgressReducerAction;
    snackbarQueue: SnackbarReducerAction;
    themeMode: ThemeReducerAction;
}

const LOCALLY_STORED: Record<keyof GlobalStateKey, boolean> = {
    backendBranches: true,
    bomCombinationRuleset: false,
    buyingProfile: false,
    cardLayers: false,
    demoMode: false,
    guides: true,
    isBomOptionsDialogOpen: false,
    msalAuthenticationResult: false,
    msalProfile: false,
    navigateWarning: false,
    permissionOverrides: true,
    progressModal: false,
    brandPickerMode: false,
    isResultCountVisible: false,
    isSearchBarVisible: false,
    resultCount: false,
    searchPlaceholder: false,
    searchQuery: false,
    selectedBrands: true,
    snackbarQueue: false,
    theme: false,
    themeMode: true,
};

// The getHookAndProviderFromSlices function is used to create a hook and a provider for our global state.
// The slices object passed to this function defines the initial state of our global state.
// Each key in the slices object represents a slice of our global state.
// The defineSlice function is used to define the initial state of each slice.
const { useSlice, Provider } = getHookAndProviderFromSlices({
    slices: {
        // The 'theme' slice is a state object that has a theme object for the ThemeProvider in each package.
        // It is initially set to the default state.
        themeMode: defineSlice<ThemeReducerState>({
            initialArg: defaultThemeState,
            isGetInitialStateFromStorage: LOCALLY_STORED.themeMode,
            reducer: themeReducer,
        }),
        // The 'theme' slice is a theme object that can be fed into a ThemeProvider in each package.
        // It is initially set to a default theme, then updated by BuyingThemeProvider.
        theme: defineSlice({
            // Initially a default theme, will be updated by BuyingThemeProvider
            initialArg: createTheme(),
            isGetInitialStateFromStorage: LOCALLY_STORED.theme,
        }),
        // The 'selectedBrands' slice is an array of brandNumbers that represents the brands selected by the user.
        // It is initially an empty array.
        selectedBrands: defineSlice({
            initialArg: [],
            isGetInitialStateFromStorage: LOCALLY_STORED.selectedBrands,
        }),
        // The 'buyingProfile' slice is an object that represents the BESTSELLER buying related user data.
        // It is initially undefined.
        buyingProfile: defineSlice({
            initialArg: undefined,
            isGetInitialStateFromStorage: LOCALLY_STORED.buyingProfile,
        }),
        // The 'permissionOverrides' slice simulates permissions in the frontend only, via the devbar.
        // It is initially empty, meaning every permission is inherited from the real buyingProfile.
        permissionOverrides: defineSlice<Record<string, boolean>>({
            initialArg: {},
            isGetInitialStateFromStorage: LOCALLY_STORED.permissionOverrides,
        }),
        // The 'msalProfile' slice is an object that represents the Microsoft related user data.
        // It is initially undefined.
        msalProfile: defineSlice<MsalProfile | undefined>({
            initialArg: undefined,
            isGetInitialStateFromStorage: LOCALLY_STORED.msalProfile,
        }),
        // The 'msalAuthenticationResult' slice is an object that represents the obtained Microsoft related user tokens.
        // It is initially undefined.
        msalAuthenticationResult: defineSlice<AuthenticationResult | undefined>(
            {
                initialArg: undefined,
                isGetInitialStateFromStorage:
                    LOCALLY_STORED.msalAuthenticationResult,
            }
        ),
        // The 'demoMode' slice is a boolean that represents whether the application is in demo mode.
        // It is initially turned off.
        demoMode: defineSlice({
            initialArg: false,
            isGetInitialStateFromStorage: LOCALLY_STORED.demoMode,
        }),
        // The 'backendBranches' slice is an object that includes key value pairs that store which branch is used for which backend.
        // Each branch is initially set to null.
        backendBranches: defineSlice<BackendBranches>({
            initialArg: getInitialBackendBranches(),
            isGetInitialStateFromStorage: false, // Handled manually in getInitialBackendBranches
        }),
        // The 'searchQuery' slice is a string that represents the search query.
        // It is initially an empty string.
        searchQuery: defineSlice<string>({
            initialArg: '',
            isGetInitialStateFromStorage: LOCALLY_STORED.searchQuery,
        }),
        // The 'searchPlaceholder' slice is a string that represents the placeholder text for the search bar.
        // It is initially set to "Enter text here".
        searchPlaceholder: defineSlice<string>({
            initialArg: 'Enter text here',
            isGetInitialStateFromStorage: LOCALLY_STORED.searchPlaceholder,
        }),
        // The 'resultCount' slice is a number that represents the total amount of results based on what page is open.
        // It is initially 0.
        resultCount: defineSlice<number>({
            initialArg: 0,
            isGetInitialStateFromStorage: false,
        }),
        // The 'isSearchBarVisible' slice toggles the header search bar. Each page opts in via useGlobalHeaderState.
        // It is initially hidden.
        isSearchBarVisible: defineSlice<boolean>({
            initialArg: false,
            isGetInitialStateFromStorage: false,
        }),
        // The 'isResultCountVisible' slice toggles the header result-count subtitle. Each page opts in via useGlobalHeaderState.
        // It is initially hidden.
        isResultCountVisible: defineSlice<boolean>({
            initialArg: false,
            isGetInitialStateFromStorage: false,
        }),
        // The 'brandPickerMode' slice controls the header brand picker: 'hidden', 'single' or 'multiple' select.
        // It is initially hidden.
        brandPickerMode: defineSlice<BrandPickerMode>({
            initialArg: 'hidden',
            isGetInitialStateFromStorage: false,
        }),
        // The 'isBomOptionsDialogOpen' slice lets the Create/Edit BOM guide open and close the
        // BOM options dialog (which lives in a separate microfrontend). It is initially closed.
        isBomOptionsDialogOpen: defineSlice<boolean>({
            initialArg: false,
            isGetInitialStateFromStorage: false,
        }),
        // The snackbarQueue is a slice that represents the queue of snackbars to be displayed.
        // Umbrella uses this to render the actual snackbars, but we can add or remove snackbars from anywhere
        snackbarQueue: defineSlice<SnackbarReducerState>({
            initialArg: [],
            isGetInitialStateFromStorage: LOCALLY_STORED.snackbarQueue,
            reducer: snackbarReducer,
        }),
        // The snackbarQueue is a slice that represents the queue of snackbars to be displayed.
        // Umbrella uses this to render the actual snackbars, but we can add or remove snackbars from anywhere
        progressModal: defineSlice<ProgressModalConfig>({
            initialArg: INACTIVE_PROGRESS_CONFIG,
            isGetInitialStateFromStorage: LOCALLY_STORED.progressModal,
            reducer: progressReducer,
        }),
        // The navigateWarning slice has two attributes to control whether the application should show a warning when navigating away from a page.
        // Micro-frontends can set the shouldShowWarning attribute to true to indicate that a warning should be shown.
        // Umbrella then checks this value and uses the showWarning to render the actual warning.
        navigateWarning: defineSlice<NavigateWarningReducerState>({
            initialArg: {
                showWarning: false,
                shouldShowWarning: false,
                confirmCallback: undefined,
            },
            isGetInitialStateFromStorage: LOCALLY_STORED.navigateWarning,
            reducer: navigateWarningReducer,
        }),
        // The cardLayers slice stores the layers of cards that should be shown in the frontend
        cardLayers: defineSlice<CardLayersReducerState>({
            initialArg: [],
            isGetInitialStateFromStorage: LOCALLY_STORED.cardLayers,
            reducer: cardLayersReducer,
        }),
        // THIS IS PROBABLY A TEMPORARY SLICE, USED FOR DEBUGGING BOM COMBINATION_RULESETS FOR ANALYSTS
        // The bomCombinationRuleset slice stores the bomCombinationRuleset data so that we can translate it in the devbar
        bomCombinationRuleset: defineSlice<
            BomCombinationRulesetContext | undefined
        >({
            initialArg: undefined,
            isGetInitialStateFromStorage: LOCALLY_STORED.bomCombinationRuleset,
        }),
        // The 'guides' slice manages the guide system: manager dialog state, running guide,
        // completion/discovery tracking, and step progress. Hydration/pruning is handled manually in
        // getInitialGuidesState so the ephemeral fields (isManagerOpen, runningGuideId) are
        // never restored — booting with the dialog "open" leaves a stuck, click-blocking modal.
        guides: defineSlice<GuidesReducerState>({
            initialArg: getInitialGuidesState(),
            isGetInitialStateFromStorage: false, // Handled manually in getInitialGuidesState so we can prune it
            reducer: guidesReducer,
        }),
    },
});

/**
 * This type is used to define the type of the dispatch function returned by the useGlobalState hook.
 * Basically, everything using a reducer and has a dispatch needs to be added here
 */
type DispatchType<K extends keyof GlobalStateKey> = K extends
    | 'cardLayers'
    | 'guides'
    | 'navigateWarning'
    | 'progressModal'
    | 'snackbarQueue'
    | 'themeMode'
    ? Dispatch<GlobalActionKey[K]>
    : Dispatch<SetStateAction<GlobalStateKey[K]>>;

const useGlobalState = <K extends keyof GlobalStateKey>(
    globalStateKey: K
): [GlobalStateKey[K], DispatchType<K>] => {
    const [globalState, setGlobalState] =
        useSlice<GlobalStateKey[K]>(globalStateKey);

    useEffect(() => {
        if (LOCALLY_STORED[globalStateKey]) {
            localStorage.setItem(globalStateKey, JSON.stringify(globalState));
        }
    }, [globalState, globalStateKey]);

    return [globalState, setGlobalState as DispatchType<K>];
};

/**
 * We export our useGlobalState hook and the 'Provider' component with a more descriptive name.
 * The 'useGlobalState' hook can be used to access and update the global state.
 * The 'GlobalStateProvider' component should be used to wrap the parts of our application that need access to the global state.
 */
export {
    BACKEND_NAMES,
    Provider as GlobalStateProvider,
    useGlobalState,
    getInitialBackendBranches,
};
export type {
    BackendBranches,
    Brand,
    BrandPickerMode,
    BuyingProfile,
    BomCombinationRulesetContext,
    AuthenticationResult as MsalAuthenticationResult,
    MsalProfile,
};
